import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import AppBreadCrumb from '../../common/AppBreadCrumb';
import AppToaster from '../../AppToaster';
import { createDebitNote, fetchDebitNote, updateDebitNote } from '../../../api/DebitNotesAPI';
import { getParties } from '../../../api/PartiesAPI';
import { getProducts } from '../../../api/ProductsAPI';
import { getHoas } from '../../../api/HoasAPI';
import { handleApiError } from '../../../api/risapi';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from 'date-fns';

const initialItem = { id: null, type: 'product', product_id: null, hoa_id: null, quantity: '', price: '', line_total: '', description: '' };
const initialState = { party_id: null, debit_note_date: new Date(), reason: '', items: [{...initialItem}], total_amount: 0, debit_note_number: null };

const DebitNoteForm = ({ formType, currentId }) => {
    const navigate = useNavigate();
    const [dn, setDn] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [toaster, setToaster] = useState({});
    const [showToaster, setShowToaster] = useState(false);
    const [partyOptions, setPartyOptions] = useState([]);
    const [productOptions, setProductOptions] = useState([]);
    const [hoaOptions, setHoaOptions] = useState([]);

    useEffect(() => {
        let isMounted = true; setLoading(true);
        Promise.all([
            new Promise(r => getParties({paginate: false}, r)),
            new Promise(r => getProducts({paginate: false}, r)),
            new Promise(r => getHoas({paginate: false}, r))
        ]).then(([partyRes, prodRes, hoaRes]) => {
            if (!isMounted) return;
            if(partyRes.parties) setPartyOptions(partyRes.parties.map(p => ({value: p.id, label: p.name})));
            if(prodRes.products) setProductOptions(prodRes.products.map(p => ({value: p.id, label: p.name})));
            if(hoaRes.hoas) setHoaOptions(hoaRes.hoas.filter(h => !h.is_system_defined).map(h => ({value: h.id, label: h.name})));
            setLoading(false);
        });

        if (currentId && formType === 'edit') {
            setLoading(true);
            fetchDebitNote(currentId, (res) => {
                if (!isMounted) return;
                setLoading(false);
                if (res.debit_note) {
                    const data = res.debit_note;
                    setDn({
                        ...data,
                        debit_note_date: parseISO(data.debit_note_date),
                        items: data.items.map(i => ({
                             ...i, type: i.product_id ? 'product' : 'account',
                             quantity: parseFloat(i.quantity)||'', price: parseFloat(i.price)||'', line_total: parseFloat(i.line_total)||''
                        }))
                    });
                }
            });
        }
        return () => { isMounted = false; };
    }, [formType, currentId]);

    const handleItemChange = (i, field, value) => {
         const newItems = [...dn.items]; newItems[i] = { ...newItems[i], [field]: value };
         if (field === 'type') { newItems[i].product_id = null; newItems[i].hoa_id = null; newItems[i].quantity = ''; newItems[i].price = ''; }
         if (field === 'quantity' || field === 'price') {
              const q = parseFloat(newItems[i].quantity) || 0; const p = parseFloat(newItems[i].price) || 0;
              if (q && p) newItems[i].line_total = (q * p).toFixed(2);
         }
         setDn(prev => ({ ...prev, items: newItems, total_amount: newItems.reduce((sum, item) => sum + (parseFloat(item.line_total)||0), 0) }));
    };
    const addItem = () => setDn({...dn, items: [...dn.items, {...initialItem}]});
    const removeItem = (i) => setDn({...dn, items: dn.items.filter((_, idx) => idx !== i)});

    const handleSave = () => {
        if (saving) return;
        setSaving(true); setErrors({});
        const apiCall = formType === 'create' ? createDebitNote : (id, data, cb) => updateDebitNote(id, data, cb);
        const payload = { ...dn, debit_note_date: format(dn.debit_note_date, 'yyyy-MM-dd') };
        apiCall(formType === 'create' ? payload : currentId, formType === 'create' ? (r) => onSave(r) : payload, (r) => onSave(r));
    };
    const onSave = (res) => {
        setSaving(false);
        if (res.debit_note) navigate('/franchisee_user/sales/debit-notes', { state: { message: `Debit Note ${formType==='create'?'created':'updated'}` } });
        else handleApiError(res, setErrors);
    };

    return (
        <>
           <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
           <main className="container-fluid">
               <AppBreadCrumb title={formType === 'create' ? 'New Debit Note' : 'Edit Debit Note'} />
               {loading && <div className="loader_box"><div className="loader_14"></div></div>}
               <div className="card">
                   <div className="card-header"><h5>{dn.debit_note_number ? `Edit #${dn.debit_note_number}` : 'Create New'}</h5></div>
                   <div className="card-body">
                       <form onSubmit={e => e.preventDefault()}>
                           <div className="row mb-3">
                               <div className="col-md-4"><label className="form-label">Supplier *</label><Select options={partyOptions} value={partyOptions.find(o=>o.value===dn.party_id)} onChange={o=>setDn({...dn, party_id:o?.value})} placeholder="Select Party"/><span className="text-danger small">{errors.party_id}</span></div>
                               <div className="col-md-4"><label className="form-label">Date *</label><DatePicker selected={dn.debit_note_date} onChange={d=>setDn({...dn, debit_note_date:d})} className="form-control" dateFormat="yyyy-MM-dd"/></div>
                               <div className="col-md-4"><label className="form-label">Reason</label><input className="form-control" value={dn.reason||''} onChange={e=>setDn({...dn, reason:e.target.value})}/></div>
                           </div>
                           <h6 className="mb-2">Details</h6>
                           <div className="table-responsive"><table className="table table-bordered table-sm"><thead className="table-light"><tr><th style={{width:'15%'}}>Type</th><th style={{width:'30%'}}>Item/Account *</th><th style={{width:'10%'}}>Qty</th><th style={{width:'10%'}}>Rate</th><th style={{width:'15%'}}>Total *</th><th style={{width:'5%'}}></th></tr></thead><tbody>
                               {dn.items.map((item, i) => (
                                   <tr key={i}>
                                       <td><select className="form-select form-select-sm" value={item.type} onChange={e=>handleItemChange(i, 'type', e.target.value)}><option value="product">Product</option><option value="account">Account (HOA)</option></select></td>
                                       <td>{item.type === 'product' ? <Select options={productOptions} value={productOptions.find(o=>o.value===item.product_id)} onChange={o=>handleItemChange(i, 'product_id', o?.value)} placeholder="Select Product" menuPortalTarget={document.body} styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}/> : <Select options={hoaOptions} value={hoaOptions.find(o=>o.value===item.hoa_id)} onChange={o=>handleItemChange(i, 'hoa_id', o?.value)} placeholder="Select Account" menuPortalTarget={document.body} styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}/>}</td>
                                       <td><input type="number" className="form-control form-control-sm" value={item.quantity} onChange={e=>handleItemChange(i, 'quantity', e.target.value)} disabled={item.type==='account'}/></td>
                                       <td><input type="number" className="form-control form-control-sm" value={item.price} onChange={e=>handleItemChange(i, 'price', e.target.value)} disabled={item.type==='account'}/></td>
                                       <td><input type="number" className="form-control form-control-sm text-end fw-bold" value={item.line_total} onChange={e=>handleItemChange(i, 'line_total', e.target.value)} /></td>
                                       <td><button className="btn btn-sm btn-outline-danger py-0" onClick={()=>removeItem(i)}>&times;</button></td>
                                   </tr>
                               ))}
                           </tbody><tfoot><tr><td colSpan={4}><button className="btn btn-sm btn-outline-primary" onClick={addItem}>+ Add Line</button></td><td className="text-end fw-bold">{parseFloat(dn.total_amount||0).toFixed(2)}</td><td></td></tr></tfoot></table></div>
                           <div className="text-end mt-3">
                            <button className="btn btn-primary me-2" onClick={handleSave} disabled={saving}>{saving?'Saving...':'Save Debit Note'}</button>
                            <button className="d-none  d-md-inline-block btn btn-light-reddit b-r-22 d-inline-flex-center" type="button" onClick={() => navigate('/franchisee_user/sales/debit-notes')} >
                          
                            Go Back</button>
                            </div>
                       </form>
                   </div>
               </div>
           </main>
        </>
    );
};
export default DebitNoteForm;